<template>
  <WidgetCadre
    titre="Factures d'achat à ventiler"
    v-bind="apparence"
    :lien="donnees?.lien || '/compta/factures-achat'"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    :etat="etat"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <p class="achats__total">
      <span class="chiffre chiffre--grand">{{ donnees?.a_ventiler ?? '—' }}</span>
      <span class="etiquette">{{ (donnees?.a_ventiler ?? 0) > 1 ? 'pièces à ventiler' : 'pièce à ventiler' }}</span>
    </p>

    <dl class="achats__details">
      <div>
        <dt class="etiquette">
          Montant TTC
        </dt>
        <dd class="chiffre chiffre--moyen">
          {{ euros(donnees?.montant_ttc) }}
        </dd>
      </div>
      <div>
        <dt class="etiquette">
          Sans compte
        </dt>
        <dd class="chiffre chiffre--moyen">
          {{ donnees?.non_rattachees ?? '—' }}
        </dd>
      </div>
      <div>
        <dt class="etiquette">
          La plus ancienne
        </dt>
        <!-- Nulle quand aucune pièce n'attend : « 0 jour » se lirait comme une
             pièce arrivée ce matin. -->
        <dd
          class="chiffre chiffre--moyen"
          :class="{ 'achats--alerte': attenteLongue }"
        >
          {{ attente }}
        </dd>
      </div>
    </dl>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('factures-achat-a-ventiler')

/** Un mois d'attente : le seuil où la pile cesse d'être un courant normal. */
const SEUIL_ALERTE = 30

const jours = computed(() =>
    typeof props.donnees?.attente_jours === 'number' ? props.donnees.attente_jours : null,
)

const attente = computed(() => {
    if (jours.value === null) return '—'

    return jours.value <= 1 ? `${jours.value} jour` : `${jours.value} jours`
})

const attenteLongue = computed(() => jours.value !== null && jours.value >= SEUIL_ALERTE)

/** Rien à ventiler est un état sain, pas une absence de données. */
const etat = computed(() => {
    if (!props.donnees) return 'repos'
    if (props.donnees.a_ventiler > 0) return 'attention'

    return 'sain'
})

function euros(montant) {
    if (montant === null || montant === undefined) return '—'

    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(montant)
}
</script>

<style lang="scss" scoped>
.achats {
  &__total {
    display: flex;
    align-items: baseline;
    gap: 9px;
    margin: 0;
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin: 12px 0 0;
    padding-top: 10px;
    border-top: 1px solid var(--el-border-color-lighter);

    dt { margin: 0; }
    dd { margin: 2px 0 0; }
  }

  &--alerte { color: var(--accueil-attention); }
}
</style>
